import dayjs from 'dayjs'
import { get } from 'lodash'
import React from 'react'

import { apiEndpoints } from 'api'
import {
  statusMap,
  commonAttributes,
  navigationPaths,
  studentSourceTypes,
  tableColumnDefaultOption,
  batchTypesMap,
  levelMap,
  // `level` was imported here but never used in this file, and it is not
  // exported by app/config. CRA's webpack returned undefined for the
  // missing export silently; Vite's ESM aborts the whole module graph
  // on bad imports. Removed.
  yesNoOptions
} from 'app/config'
import { sanitize } from 'app/utils'
import { validateName, validateNotEmpty, validateFee } from 'app/validators'

export const columns = [
  {
    ...commonAttributes.uuid,
    options: {
      ...commonAttributes.uuid.options,
      display: false
    }
  },
  {
    name: 'program_enrolment.batch.uuid',
    formDataKey: 'batch',
    label: 'Batch',
    editable: false,
    resultStructure: {
      keyName: 'batch',
      keyValue: 'uuid'
    },
    options: {
      display: false
    }
  },
  {
    name: 'student.user.uuid',
    label: 'Student uuid',
    editable: false,
    options: {
      display: false
    }
  },
  {
    name: 'student.user.first_name',
    label: 'Student First Name',
    disabledInFormOnEdit: true,
    validate: validateName,
    options: {
      display: false
    }
  },
  {
    name: 'student.user.last_name',
    label: 'Student Last Name',
    disabledInFormOnEdit: true,
    validate: validateName,
    options: {
      display: false
    }
  },
  {
    name: 'student.user',
    label: 'Student Name',
    disabledInForm: true,
    options: {
      ...tableColumnDefaultOption.options,
      customBodyRender: (value) => (
        <span>{`${(value && value.first_name) || ''} ${
          (value && value.last_name) || ''
        }`}</span>
      ),
      filterKey: 'student_name'
    }
  },
  {
    name: 'student.parent.user',
    label: 'Parent Name',
    disabledInForm: true,
    validate: validateNotEmpty,
    options: {
      filterKey: 'parent_name',
      customBodyRender: (value) =>
        value ? (
          <span>
            <a
              href={`${navigationPaths.parentdashboard}?parent=${value.uuid}`}
            >{`${value.first_name} ${value.last_name}`}</a>
          </span>
        ) : null,
      ...tableColumnDefaultOption.options
    }
  },
  {
    name: 'student.parent.user.phone_number',
    label: 'Parent Number',
    disabledInForm: true,
    validate: validateNotEmpty,
    options: {
      filterKey: 'parent_phone_number',
      ...tableColumnDefaultOption.options
    }
  },
  {
    name: 'program_enrolment.batch.teacher.user.full_name',
    label: 'Teacher Name',
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'teacher_name'
    },
    disabledInForm: true
  },
  {
    name: 'program_enrolment.batch.course.name',
    label: 'Course Name',
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'course_name'
    },
    disabledInForm: true
  },
  {
    name: 'program_enrolment.batch.cadence',
    label: 'Course Timing',
    disabledInForm: true,
    options: {
      ...tableColumnDefaultOption.options,
      // filterKey: 'batch_timings',
      disableFilter: true,
      customBodyRender: (value) => {
        const start = value?.[0]?.start_time?.split('-')?.join(':')
        const end = value?.[0]?.end_time?.split('-')?.join(':')
        const timings = `${dayjs('1/1/1 ' + start).format(
          'h:mma'
        )} - ${dayjs('1/1/1 ' + end).format('h:mma')}`
        return value ? (
          <span>
            {timings}
          </span>
        ) : null
      }
    }
  },
  {
    ...commonAttributes.centre,
    name: 'program_enrolment.batch.centre.name',
    label: 'Centre',
    disabledInForm: true,
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'centre_name'
    }
  },
  {
    name: 'status',
    label: 'Status',
    widget: 'select',
    widgetFilter: true,
    valueLabelMappings: statusMap,
    disabledInForm: true,
    ...tableColumnDefaultOption
  },
  // Feature 5 — AIM-only seat block. Filter chip on the existing list
  // page; backend resolves `is_seat_block=true` via the SQL filter in
  // student_admission.filter_seat_block_admissions (active +
  // admission_fee_paid + first instalment PENDING).
  {
    name: 'is_seat_block',
    label: 'Seat Block',
    widget: 'select',
    widgetFilter: true,
    // Send the option KEY ('true'/'false') to the backend, not the display
    // label ('yes'/'no'). Without this the filter chip submitted
    // `is_seat_block=yes`, which Django's BooleanFilter can't parse, so it
    // silently no-ops and returns every admission instead of just seat blocks.
    isValueKey: true,
    valueLabelMappings: yesNoOptions,
    disabledInForm: true,
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'is_seat_block',
      // Render the authoritative backend flag
      // (StudentAdmissionDetailSerializer.is_seat_block = active + AIM paid +
      // Term 1 PENDING). The previous version reconstructed this from
      // tableMeta.tableData[rowIndex], but in mui-datatables v3 that element
      // is a { index, data: [...] } wrapper — so row.status /
      // row.admission_fee_paid were always undefined and the column rendered
      // "No" for every row, including genuine seat blocks (the bug ops hit on
      // the school-care enrolment screen).
      customBodyRender: (value) => <span>{value ? 'Yes' : 'No'}</span>
    }
  },
  {
    ...commonAttributes.date,
    name: 'created',
    label: 'Booking Date',
    widget: 'datepicker',
    validate: validateNotEmpty,
    options: {
      ...commonAttributes.date.options,
      filterKey: 'created_date',
      customBodyRender: (value) => {
        const timings = `${dayjs(value).format(
          'YYYY-MM-DD'
        )}`
        return value ? (
          <span>
            {timings}
          </span>
        ) : null
      }
    }
  },
  {
    ...commonAttributes.date,
    name: 'start_date',
    label: 'Start Date',
    widget: 'datepicker',
    validate: validateNotEmpty
  },
  {
    ...commonAttributes.date,
    name: 'end_date',
    label: 'End Date',
    disabledInForm: true
  },
  {
    ...commonAttributes.date,
    name: 'adjusted_end_date',
    label: 'New End Date',
    disabledInFormOnEdit: true
  },
  // {
  //   name: 'is_termination_requested',
  //   label: 'Termination Requested',
  //   validate: validateNotEmpty,
  //   disabledInFormOnEdit: true,
  //   options: {
  //     ...tableColumnDefaultOption.options,
  //     disableFilter: true,
  //     customBodyRender: (value) => {
  //       return <div>{value ? 'Yes' : 'No'}</div>
  //     }
  //   }
  // },
  // {
  //   name: 'termination_requested_on',
  //   label: 'Termination Requested On',
  //   validate: validateNotEmpty,
  //   disabledInFormOnEdit: true,
  //   options: {
  //     ...tableColumnDefaultOption.options,
  //     disableFilter: true,
  //     customBodyRender: (value) => {
  //       return <div>{value}</div>
  //     }
  //   }
  // },
  {
    name: 'termination_reason',
    label: 'Termination Reason',
    validate: validateNotEmpty,
    disabledInFormOnEdit: true,
    options: {
      ...tableColumnDefaultOption.options,
      disableFilter: true,
      customBodyRender: (value) => {
        return <div dangerouslySetInnerHTML={{ __html: sanitize(value) }} />
      },
      setCellProps: () => ({
        style: {
          minWidth: '200px',
          maxWidth: '200px',
          whiteSpace: 'pre-wrap'
        }
      })
    }
  },
  {
    name: 'termination_notes',
    label: 'Termination Notes',
    validate: validateNotEmpty,
    disabledInFormOnEdit: true,
    options: {
      ...tableColumnDefaultOption.options,
      disableFilter: true,
      customBodyRender: (value) => {
        return <div dangerouslySetInnerHTML={{ __html: sanitize(value) }} />
      },
      setCellProps: () => ({
        style: {
          minWidth: '500px',
          maxWidth: '500px',
          whiteSpace: 'pre-wrap'
        }
      })
    }
  },
  {
    name: 'terminated_by',
    label: 'Terminated By',
    validate: validateNotEmpty,
    disabledInFormOnEdit: true,
    options: {
      ...tableColumnDefaultOption.options,
      disableFilter: true,
      customBodyRender: (value) => {
        if (!value) return <div>NA</div>
        return <div>{value?.full_name}</div>
      }
    }
  },
  // {
  //   name: 'termination_source',
  //   label: 'Termination Source',
  //   validate: validateNotEmpty,
  //   disabledInFormOnEdit: true,
  //   options: {
  //     ...tableColumnDefaultOption.options,
  //     disableFilter: true,
  //     customBodyRender: (value) => {
  //       return <div>{value}</div>
  //     }
  //   }
  // }
]
